import { Autocomplete, TextField, AutocompleteProps } from "@mui/material";
import { useState, useCallback, useEffect } from "react";
import PlacesAutocomplete from "react-places-autocomplete";
import { ConditionValueSelectorComponentProps } from "../ConditionForm";
import { useTranslation } from "react-i18next";
import { trim } from "lodash";
import { testIds } from "./CityInput.testIds";

const CityInput = ({
  value,
  onChange,
  label,
  ...otherProps
}: ConditionValueSelectorComponentProps &
  Pick<AutocompleteProps<string, false, undefined, undefined>, "sx">) => {
  const { t } = useTranslation("common");
  const [inputValue, setInputValue] = useState(value?.toString() || "");

  // Sync inputValue with the external value prop
  useEffect(() => {
    setInputValue(value?.toString() || "");
  }, [value]);

  const onAutocompleteInputChange = useCallback(
    (newValue: string) => {
      setInputValue(newValue);
      if (!newValue) {
        onChange(null);
      }
    },
    [onChange]
  );

  const onSelect = useCallback(
    (address: string, placeId: string) => {
      const placesService = new google.maps.places.PlacesService(
        new google.maps.Map(document.createElement("div"))
      );
      placesService.getDetails(
        {
          placeId: placeId,
        },
        (place) => {
          if (!place) {
            return;
          }
          //   Only keep the city and state from the address
          onChange(
            place.formatted_address
              ?.split(",")
              .map(trim)
              .slice(0, 2)
              .join(", ") || ""
          );
        }
      );
      //   setInputValue(address);
    },
    [onChange]
  );

  return (
    <PlacesAutocomplete data-testid="city-input-places-autocomplete"
      value={inputValue}
      onChange={onAutocompleteInputChange}
      onSelect={onSelect}
      searchOptions={{
        types: ["(cities)"], // Restrict search to cities only
      }}
    >
      {({ getInputProps, suggestions, loading }) => {
        const inputProps = getInputProps();
        return (
          <Autocomplete
            data-testid={testIds.cityInputAutocomplete}
            options={suggestions}
            fullWidth
            filterOptions={(x) => x}
            clearOnEscape={false}
            clearOnBlur={false}
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                label={label || t("address.city", "City")}
                placeholder="Search cities..."
                inputProps={{
                  ...params.inputProps,
                  "data-testid": testIds.cityInputAutocompleteInput,
                }}
              />
            )}
            inputValue={inputProps.value || ""}
            onInputChange={(event, textValue) => {
              inputProps.onChange({
                target: {
                  value: textValue,
                },
              });
            }}
            loading={loading}
            getOptionLabel={(option) => {
              // Only keep the city and state from the address
              return (
                option.description
                  .split(",")
                  .map(trim)
                  .slice(0, 2)
                  .join(", ") || ""
              );
            }}
            renderOption={(props, suggestion) => (
              <li
                {...props}
                data-testid={testIds.cityInputAutocompleteOption}
                data-value={suggestion.placeId}
              >
                {suggestion.description
                  .split(",")
                  .map(trim)
                  .slice(0, 2)
                  .join(", ")}
              </li>
            )}
            onChange={(event, suggestion) => {
              if (!suggestion) {
                return;
              }
              onSelect(inputValue, suggestion.placeId);
            }}
            noOptionsText={t("address.noOptions", "No options")}
            {...otherProps}
          />
        );
      }}
    </PlacesAutocomplete>
  );
};

export default CityInput;
